import { apiClient, type RequestOptions } from '@/services/apiClient';

export type RiskLevel = 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';
export type RealtimeErrorKind = 'permission' | 'offline' | 'error';

export interface RealtimeEventItem {
  id: string;
  eventName: string;
  topic: string;
  tenantId: string;
  entityType: string;
  entityId: string | null;
  actorId: string | null;
  recipientUserId: string | null;
  severity: RiskLevel | null;
  permission: string | null;
  timestamp: string;
  payload: unknown;
}

export interface ProjectFinancials {
  contractValue: string;
  budgetCurrent: string;
  actualCost: string;
  forecastAtCompletion: string;
  budgetVariance: string;
}

export interface ProjectMapMarker {
  id: string;
  canonicalClientIdentityId?: string | null;
  projectNumber: string;
  name: string;
  clientName: string | null;
  sector: string | null;
  status: string;
  phase: string;
  riskLevel: RiskLevel | null;
  healthScore: number | null;
  cohort?: ProjectMapCohort;
  source?: 'project' | 'lead' | 'estimate';
  sourceId?: string;
  financials: ProjectFinancials | null;
  schedule: {
    startDate: string | null;
    substantialCompletion: string | null;
    daysToCompletion: number | null;
  };
  location: {
    city: string | null;
    state: string | null;
    latitude: number;
    longitude: number;
  };
  activeAlerts: number | null;
}

export type ProjectMapCohort =
  | 'completed'
  | 'active'
  | 'submitted_bid'
  | 'awarded_pending'
  | 'pursuit'
  | 'estimating'
  | 'lost_bid'
  | 'no_bid'
  | 'on_hold'
  | 'cancelled'
  | 'planning';
export interface ProjectMapCohortSummary {
  cohort: ProjectMapCohort;
  availability: 'complete' | 'partial' | 'restricted';
  records: number | null;
  measuredRecords: number;
  plotted: number;
  missingCoordinates: number | null;
}
export interface ProjectMapUnlocated {
  id: string;
  canonicalClientIdentityId?: string | null;
  sourceId: string;
  source: 'project' | 'lead' | 'estimate';
  projectNumber: string;
  name: string;
  cohort: ProjectMapCohort;
  city: string | null;
  state: string | null;
  clientName?: string | null;
  sector?: string | null;
  status?: string;
  phase?: string;
  riskLevel?: RiskLevel | null;
  schedule?: ProjectMapMarker['schedule'];
  financials?: ProjectMapMarker['financials'];
  activeAlerts?: number | null;
}

export interface ProjectMapResponse {
  generatedAt: string;
  tenant: { id: string; name: string } | null;
  map: {
    center: { latitude: number; longitude: number } | null;
    bounds: { north: number; south: number; east: number; west: number } | null;
  };
  metrics: {
    projects: number;
    criticalProjects: number;
    openAlerts: number;
    portfolioValue: string | null;
    forecastVariance: string | null;
  };
  markers: ProjectMapMarker[];
  cohorts?: ProjectMapCohortSummary[];
  unlocated?: ProjectMapUnlocated[];
  coverage?: {
    projects: 'complete' | 'partial';
    bids: 'complete' | 'partial' | 'restricted';
    basis: string;
  };
}

export class RealtimeApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'RealtimeApiError';
  }
}

export function realtimeErrorKind(error: unknown): RealtimeErrorKind {
  if (error instanceof RealtimeApiError && error.status === 403) {
    return 'permission';
  }
  if (error instanceof RealtimeApiError && error.status === 0) {
    return 'offline';
  }
  return 'error';
}

async function requireData<T>(path: string, options?: RequestOptions): Promise<T> {
  const result = await apiClient.get<T>(path, options);
  if (!result.ok || result.data === null) {
    throw new RealtimeApiError(result.error ?? 'Realtime API request failed', result.status);
  }
  return result.data;
}

export const realtimeClient = {
  getEvents(limit = 30, options?: RequestOptions): Promise<RealtimeEventItem[]> {
    return requireData<RealtimeEventItem[]>(`/realtime/events?limit=${limit}`, options);
  },
  getProjectMap(options?: RequestOptions): Promise<ProjectMapResponse> {
    return requireData<ProjectMapResponse>('/realtime/project-map/cohorts', options);
  },
};
